// M02/M04/M05/M06/M07/M09/M10 — intelligence kits, builders and analyzers.
import { useMemo, useState } from "react";
import { inr } from "./engines";
import { Field, ResultRow, ToolSection, Note, copyText } from "./ui";

function CopyButton({ getText, testid }) {
  const [state, setState] = useState("idle");
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={async () => { setState((await copyText(getText())) ? "done" : "fail"); setTimeout(() => setState("idle"), 2500); }}
      className="text-sm font-medium underline underline-offset-4"
    >
      {state === "done" ? "Copied" : state === "fail" ? "Select & copy manually" : "Copy output"}
    </button>
  );
}

/* ---------- M02 Needs-Based Segment Builder ---------- */
const M02_FIELDS = [
  ["who", "Who they are (situation, not demographics)"],
  ["job", "The job they're hiring you for"],
  ["trigger", "What triggers the search"],
  ["barrier", "What stops them today"],
  ["proof", "Evidence you have (or 'assumption')"],
];
export function ToolM02() {
  const [cards, setCards] = useState([
    { name: "The Replenisher", who: "Working parent, weekly stock-up trip", job: "Never run out of the household staple", trigger: "Pantry nearly empty before the weekend", barrier: "Doesn't trust delivery slots for heavy items", proof: "assumption" },
    { name: "The Experimenter", who: "Young professional, discovery-led", job: "Feel smart about trying something better", trigger: "Social proof from a trusted creator", barrier: "Fear of wasting money on a full-size pack", proof: "3 interview quotes, Apr 2026" },
  ]);
  const setC = (i, k) => (e) => setCards((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: e.target.value } : x)));
  return (
    <div data-testid="tool-M02">
      <div className="grid md:grid-cols-2 gap-5">
        {cards.map((c, i) => (
          <div key={i} className="border border-zinc-200 p-5 bg-white" data-testid={`m02-card-${i}`}>
            <input value={c.name} onChange={setC(i, "name")} className="w-full font-serif-display text-xl outline-none border-b border-transparent focus:border-black pb-1" data-testid={`m02-name-${i}`} placeholder="Segment name" />
            <div className="mt-4 space-y-3">
              {M02_FIELDS.map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">{label}</span>
                  <textarea value={c[k]} onChange={setC(i, k)} rows={2} className="mt-1 w-full border border-zinc-200 p-2 text-sm outline-none focus:border-black" data-testid={`m02-${k}-${i}`} />
                </label>
              ))}
            </div>
            <div className={`mt-3 text-xs font-medium uppercase tracking-wide ${String(c.proof).toLowerCase().includes("assumption") ? "text-amber-700" : "text-emerald-700"}`} data-testid={`m02-proof-${i}`}>
              {String(c.proof).toLowerCase().includes("assumption") ? "Assumption — validate before betting budget" : "Evidence-backed"}
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => setCards((s) => [...s, { name: `Segment ${s.length + 1}`, who: "", job: "", trigger: "", barrier: "", proof: "assumption" }])} className="mt-5 text-sm font-medium underline underline-offset-4" data-testid="m02-add">+ Add segment</button>
      <Note>A segment you can't reach, price or serve differently is a description, not a segment. Two to four needs-based segments beat nine demographic ones.</Note>
    </div>
  );
}

/* ---------- M04 JTBD Interview Question Bank ---------- */
const M04_BANK = [
  { phase: "Opening", qs: ["Walk me through the last time you bought [category] — start from the moment you realised you needed it.", "What was happening in your life or work that week?", "Who else was involved in that decision?"] },
  { phase: "The trigger", qs: ["What specifically happened that made 'someday' become 'now'?", "What were you using before, and what made it stop being good enough?", "What were you afraid of when you started looking?"] },
  { phase: "The search", qs: ["Where did you look first — and why there?", "What did you type, ask or compare?", "Which options did you reject immediately, and what disqualified them?"] },
  { phase: "The decision", qs: ["What nearly stopped you from buying at all?", "What did you end up paying — and what made that feel acceptable?", "If the chosen option had been unavailable, what would you have done instead?"] },
  { phase: "After the purchase", qs: ["What did you do with it in the first 24 hours?", "What would make you switch away from what you bought?", "Who have you told about it, and what exactly did you say?"] },
  { phase: "Probes (use liberally)", qs: ["Can you give me a specific example?", "What did you do next?", "Why that one and not the cheaper one?", "What would have made that easier?"] },
];
export function ToolM04() {
  const [selected, setSelected] = useState(["0-0", "1-0", "3-0"]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const script = M04_BANK.flatMap((sec, si) => sec.qs.map((q, qi) => ({ id: `${si}-${qi}`, sec: sec.phase, q }))).filter((x) => selected.includes(x.id));
  return (
    <div data-testid="tool-M04">
      <p className="text-sm text-zinc-600 mb-5">Build your script: pick 8–12 questions. Interview about the last real purchase — never ask "would you buy…".</p>
      <div className="space-y-6">
        {M04_BANK.map((sec, si) => (
          <div key={sec.phase}>
            <div className="text-xs font-medium uppercase tracking-widest text-zinc-500">{sec.phase}</div>
            <div className="mt-2 space-y-2">
              {sec.qs.map((q, qi) => {
                const id = `${si}-${qi}`;
                const on = selected.includes(id);
                return (
                  <button key={id} type="button" onClick={() => toggle(id)} data-testid={`m04-q-${id}`}
                    className={`block w-full text-left border p-3 text-sm leading-relaxed transition-colors ${on ? "border-black bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                    <span className="text-xs uppercase tracking-wide text-zinc-400 mr-2">{on ? "In script" : "Add"}</span>{q}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {script.length > 0 && (
        <ToolSection title={`Your interview script (${script.length} questions)`} testid="m04-script">
          <ol className="list-decimal pl-5 space-y-2 text-sm text-zinc-700">
            {script.map((s) => <li key={s.id}><span className="text-zinc-400 text-xs uppercase mr-2">{s.sec}</span>{s.q}</li>)}
          </ol>
          <div className="mt-4"><CopyButton testid="m04-copy" getText={() => `JTBD Interview Script\n\n${script.map((s, i) => `${i + 1}. [${s.sec}] ${s.q}`).join("\n")}`} /></div>
        </ToolSection>
      )}
      <Note tone="warn">Coding worksheet: after each interview, log the trigger, the "hired" job, the fired alternative and the anxieties — in the customer's words. Ten coded interviews reveal more than a hundred survey rows.</Note>
    </div>
  );
}

/* ---------- M05 Win/Loss Interview Kit ---------- */
const M05_PROTOCOLS = [
  { type: "Won deals", tone: "good", qs: ["What was the deciding factor in the final week — the thing that made you sign?", "Which competitor came closest, and what did they do well?", "What did we do that you wish everyone did?", "What almost made this go the other way?"] },
  { type: "Lost deals", tone: "bad", qs: ["When did you first sense it wouldn't be us — and what caused it?", "What did the winner offer that we couldn't or didn't?", "Was price the reason, or the excuse?", "What would have had to be true for us to win?"] },
  { type: "No-decision deals", tone: "warn", qs: ["What made 'do nothing' the safest choice?", "What was the initiative this lost budget to?", "Who was championing change internally, and what happened to them?", "What would reopen this — and when?"] },
];
export function ToolM05() {
  const [tallies, setTallies] = useState({});
  const themes = ["Price / value gap", "Missing capability", "Relationship strength", "Timing / budget", "Risk & trust", "Delivery confidence"];
  const bump = (t, d) => setTallies((s) => ({ ...s, [t]: Math.max(0, (s[t] || 0) + d) }));
  const total = Object.values(tallies).reduce((s, v) => s + v, 0);
  return (
    <div data-testid="tool-M05">
      <div className="grid md:grid-cols-3 gap-5">
        {M05_PROTOCOLS.map((p) => (
          <div key={p.type} className="border border-zinc-200 p-5" data-testid={`m05-${p.tone}`}>
            <div className="font-serif-display text-lg">{p.type}</div>
            <ul className="mt-3 space-y-2.5 text-sm text-zinc-700 leading-relaxed">
              {p.qs.map((q, i) => <li key={i} className="flex gap-2"><span className="text-zinc-400">{i + 1}.</span>{q}</li>)}
            </ul>
          </div>
        ))}
      </div>
      <ToolSection title="Coding table — tally the real reason for each closed deal" testid="m05-tally">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {themes.map((t) => (
            <div key={t} className="border border-zinc-200 p-3 flex items-center justify-between" data-testid={`m05-theme-${t.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <span className="text-sm">{t}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => bump(t, -1)} className="w-7 h-7 border border-zinc-300 text-sm" aria-label={`decrease ${t}`}>−</button>
                <span className="w-8 text-center tabular-nums text-sm font-medium">{tallies[t] || 0}</span>
                <button type="button" onClick={() => bump(t, 1)} className="w-7 h-7 border border-zinc-300 text-sm" aria-label={`increase ${t}`}>+</button>
              </div>
            </div>
          ))}
        </div>
        {total > 0 && (
          <div className="mt-5 max-w-md" data-testid="m05-result">
            {themes.map((t) => (
              <ResultRow key={t} label={t} value={`${Math.round(((tallies[t] || 0) / total) * 100)}%`} strong={(tallies[t] || 0) === Math.max(...Object.values(tallies))} />
            ))}
          </div>
        )}
      </ToolSection>
      <Note>Rules that keep this honest: interview within 30 days of the decision; the interviewer must not be the rep who owned the deal; 15–20 interviews per quarter beats an annual deep-dive.</Note>
    </div>
  );
}

/* ---------- M06 Voice-of-Customer Analyzer ---------- */
const M06_THEMES = ["Quality / performance", "Price / value", "Delivery / availability", "Service / support", "Packaging / usability", "Trust / brand"];
export function ToolM06() {
  const [raw, setRaw] = useState("Delivery was two days late and the box arrived damaged\nGreat product but pricey compared to alternatives\nSupport resolved my issue in one call\nHalf the quantity for the same price now\nCan't find it in my local store anymore");
  const [tags, setTags] = useState({});
  const lines = useMemo(() => raw.split("\n").map((l) => l.trim()).filter(Boolean), [raw]);
  const counts = useMemo(() => {
    const c = Object.fromEntries(M06_THEMES.map((t) => [t, 0]));
    Object.values(tags).forEach((t) => { if (c[t] !== undefined) c[t]++; });
    return c;
  }, [tags]);
  const tagged = Object.keys(tags).length;
  return (
    <div data-testid="tool-M06">
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <span className="text-sm font-medium text-zinc-800">Paste reviews, complaints or call notes — one per line</span>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={8} className="mt-2 w-full border border-zinc-300 p-3 text-sm outline-none focus:border-black" data-testid="m06-input" />
          <p className="mt-2 text-xs text-zinc-500">Tag each line to a theme. The tool counts — your judgment classifies. No black-box sentiment scores.</p>
        </div>
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="m06-lines">
          {lines.map((l, i) => (
            <div key={i} className="border border-zinc-200 p-3" data-testid={`m06-line-${i}`}>
              <div className="text-sm text-zinc-700 leading-relaxed">{l}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {M06_THEMES.map((t) => (
                  <button key={t} type="button" onClick={() => setTags((s) => ({ ...s, [i]: t }))}
                    className={`text-[11px] px-2 py-1 border transition-colors ${tags[i] === t ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                    data-testid={`m06-tag-${i}-${t.replace(/[^a-z]/gi, "-").toLowerCase()}`}>{t}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 max-w-lg" data-testid="m06-result">
        <ResultRow label="Lines tagged" value={`${tagged} / ${lines.length}`} strong={tagged === lines.length && lines.length > 0} />
        {M06_THEMES.map((t) => (
          <ResultRow key={t} label={t} value={`${counts[t]} (${tagged ? Math.round((counts[t] / Math.max(1, tagged)) * 100) : 0}%)`} tone={counts[t] === Math.max(...Object.values(counts)) && counts[t] > 0 ? "bad" : undefined} />
        ))}
      </div>
      <Note>Frequency tells you how loud a theme is; severity tells you how much it costs. A theme mentioned by 10% that kills renewals outranks a 40% annoyance. Act on the intersection.</Note>
    </div>
  );
}

/* ---------- M07 Shopper Safari Toolkit ---------- */
const M07_SECTIONS = [
  { name: "Approach & location", items: ["How visible is the store/category from the street or mall corridor?", "What competes for attention in the first 10 seconds?", "Who is shopping here right now — alone, families, staff?"] },
  { name: "Navigation", items: ["How long to find the category? Count the seconds.", "What did the shopper pick up and put back?", "Which route do most shoppers take — follow three of them."] },
  { name: "The shelf", items: ["Share of shelf vs share of sales — sketch it.", "Which packs are at eye level, and whose?", "Any out-of-stocks? Photograph the gaps."] },
  { name: "Price & promotion", items: ["What price signals are visible without picking up a pack?", "Which promotions are running — and are they actually implemented?", "What does the shopper compare against?"] },
  { name: "The moment of choice", items: ["What did they finally buy, and what did they reject last?", "Did they ask anyone for help? About what?", "What would have changed their choice? (Ask one, politely.)"] },
];
export function ToolM07() {
  const [notes, setNotes] = useState({});
  const [hypotheses, setHypotheses] = useState("");
  const filled = Object.values(notes).filter((v) => String(v).trim()).length;
  return (
    <div data-testid="tool-M07">
      <p className="text-sm text-zinc-600 mb-5">One store, one hour, one form. Print it or fill it here — then debrief within 24 hours while memory is fresh.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {M07_SECTIONS.map((sec, si) => (
          <div key={sec.name} className="border border-zinc-200 p-5" data-testid={`m07-sec-${si}`}>
            <div className="font-serif-display text-lg">{sec.name}</div>
            <ul className="mt-3 space-y-1.5 text-sm text-zinc-600 list-disc pl-5">
              {sec.items.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
            <textarea value={notes[si] || ""} onChange={(e) => setNotes((s) => ({ ...s, [si]: e.target.value }))} rows={2} placeholder="Field notes…" className="mt-3 w-full border border-zinc-200 p-2 text-sm outline-none focus:border-black" data-testid={`m07-notes-${si}`} />
          </div>
        ))}
      </div>
      <ToolSection title="Debrief — hypotheses to test" testid="m07-debrief">
        <textarea value={hypotheses} onChange={(e) => setHypotheses(e.target.value)} rows={3} placeholder="Each observation → a falsifiable hypothesis. 'Shoppers miss our pack because…'" className="w-full border border-zinc-300 p-3 text-sm outline-none focus:border-black" data-testid="m07-hypo" />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-zinc-500">{filled}/5 sections completed</span>
          <CopyButton testid="m07-copy" getText={() => `SHOPPER SAFARI DEBRIEF\n\n${M07_SECTIONS.map((s, i) => `${s.name}\n${notes[i] || "(no notes)"}`).join("\n\n")}\n\nHYPOTHESES\n${hypotheses || "(none)"}`} />
        </div>
      </ToolSection>
    </div>
  );
}

/* ---------- M09 Intelligence One-Page Brief Builder ---------- */
const M09_PARTS = [
  ["decision", "The decision this informs", "One sentence. If there's no decision, there's no brief."],
  ["finding", "The finding", "What we now know that we didn't."],
  ["evidence", "The evidence", "Source, date, sample. '12 interviews, June 2026' — not 'research shows'."],
  ["sowhat", "So what", "Why this changes the decision."],
  ["action", "Recommended action", "Owner, next step, date."],
];
export function ToolM09() {
  const [v, setV] = useState({ decision: "", finding: "", evidence: "", sowhat: "", action: "" });
  const [confidence, setConfidence] = useState(2);
  const [openQ, setOpenQ] = useState("");
  const labels = ["Speculative", "Indicative", "Confident", "Proven"];
  return (
    <div data-testid="tool-M09" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        {M09_PARTS.map(([k, label, hint]) => (
          <label key={k} className="block">
            <span className="text-sm font-medium text-zinc-800">{label}</span>
            <span className="block text-xs text-zinc-500 mt-0.5">{hint}</span>
            <textarea value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid={`m09-${k}`} />
          </label>
        ))}
        <div>
          <span className="text-sm font-medium text-zinc-800">Confidence level</span>
          <div className="mt-2 flex gap-2" data-testid="m09-confidence">
            {labels.map((l, i) => (
              <button key={l} type="button" onClick={() => setConfidence(i)} className={`px-3 py-1.5 text-xs border transition-colors ${confidence === i ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`} data-testid={`m09-conf-${i}`}>{l}</button>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="text-sm font-medium text-zinc-800">Open questions</span>
          <textarea value={openQ} onChange={(e) => setOpenQ(e.target.value)} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid="m09-openq" />
        </label>
      </div>
      <div>
        <div className="border-2 border-black p-6 bg-white" data-testid="m09-preview">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Intelligence brief · confidence: {labels[confidence]}</div>
          <div className="mt-4 space-y-4">
            {M09_PARTS.map(([k, label]) => (
              <div key={k}>
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-400">{label}</div>
                <p className="mt-1 text-sm leading-relaxed text-zinc-800">{v[k] || <span className="text-zinc-300 italic">Not written yet</span>}</p>
              </div>
            ))}
            {openQ && <div><div className="text-xs font-medium uppercase tracking-widest text-zinc-400">Still open</div><p className="mt-1 text-sm text-zinc-800">{openQ}</p></div>}
          </div>
        </div>
        <div className="mt-4"><CopyButton testid="m09-copy" getText={() => `INTELLIGENCE BRIEF — confidence: ${labels[confidence]}\n\n${M09_PARTS.map(([k, l]) => `${l.toUpperCase()}\n${v[k] || "-"}`).join("\n\n")}\n\nOPEN QUESTIONS\n${openQ || "-"}`} /></div>
      </div>
    </div>
  );
}

/* ---------- M10 Decision-First Research Brief Generator ---------- */
const M10_PARTS = [
  ["decision", "The decision to be made", "e.g. 'Enter Tier-2 with the premium line in Q3, yes/no?'"],
  ["known", "What we already know", "Existing data, past studies, sales patterns."],
  ["hypotheses", "Hypotheses to test", "Statements that can be proven wrong — not topics to explore."],
  ["method", "Method & sample", "e.g. 12 JTBD interviews + 300-respondent survey in 2 cities."],
  ["accept", "Acceptance criteria", "What result would confirm / kill each hypothesis."],
  ["constraints", "Timeline & budget", "Decision date first, then work backwards."],
];
export function ToolM10() {
  const [v, setV] = useState({ decision: "", known: "", hypotheses: "", method: "", accept: "", constraints: "" });
  return (
    <div data-testid="tool-M10" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        {M10_PARTS.map(([k, label, hint]) => (
          <label key={k} className="block">
            <span className="text-sm font-medium text-zinc-800">{label}</span>
            <span className="block text-xs text-zinc-500 mt-0.5">{hint}</span>
            <textarea value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} rows={2} className="mt-1.5 w-full border border-zinc-300 p-2.5 text-sm outline-none focus:border-black" data-testid={`m10-${k}`} />
          </label>
        ))}
      </div>
      <div>
        <div className="border border-zinc-300 bg-[#fafafa] p-6" data-testid="m10-preview">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Agency-ready research brief</div>
          <div className="mt-4 space-y-4">
            {M10_PARTS.map(([k, label]) => (
              <div key={k}>
                <div className="text-xs font-medium uppercase tracking-widest text-zinc-400">{label}</div>
                <p className="mt-1 text-sm leading-relaxed text-zinc-800">{v[k] || <span className="text-zinc-300 italic">Not written yet</span>}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4"><CopyButton testid="m10-copy" getText={() => `RESEARCH BRIEF\n\n${M10_PARTS.map(([k, l]) => `${l.toUpperCase()}\n${v[k] || "-"}`).join("\n\n")}`} /></div>
        <Note>If a research proposal comes back describing methodology before restating your decision, send it back. Method serves the decision, never the reverse.</Note>
      </div>
    </div>
  );
}

/* ---------- M08 Competitor Weakness & Strategic Gap Mapper ---------- */
const M08_DEFAULT = [
  { claim: "Delivery promise kept", us: 4, rivals: [{ name: "Rival A", score: 2, evidence: "12 complaints, Jun 2026" }, { name: "Rival B", score: 3, evidence: "" }] },
  { claim: "After-sales response < 24h", us: 3, rivals: [{ name: "Rival A", score: 2, evidence: "" }, { name: "Rival B", score: 1, evidence: "Trustpilot pattern, Q2" }] },
  { claim: "Price transparency", us: 2, rivals: [{ name: "Rival A", score: 4, evidence: "" }, { name: "Rival B", score: 4, evidence: "Rate card public" }] },
];
export function ToolM08() {
  const [rows, setRows] = useState(M08_DEFAULT);
  const setRow = (i, k) => (e) => setRows((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: e.target?.value ?? e } : x)));
  const setRival = (i, ri, k) => (e) => setRows((s) => s.map((x, xi) => (xi === i ? { ...x, rivals: x.rivals.map((r, rj) => (rj === ri ? { ...r, [k]: e.target.value } : r)) } : x)));
  const gaps = useMemo(() => {
    const out = [];
    rows.forEach((r) => {
      const weakest = r.rivals.reduce((m, x) => (Number(x.score) < Number(m.score) ? x : m), r.rivals[0]);
      const usScore = Number(r.us);
      const rivalBest = Math.max(...r.rivals.map((x) => Number(x.score)));
      if (usScore > rivalBest) out.push({ type: "our-strength", text: `${r.claim}: we lead (${usScore} vs best rival ${rivalBest}) — make it provable in market.` });
      else if (usScore < Math.min(...r.rivals.map((x) => Number(x.score)))) out.push({ type: "our-gap", text: `${r.claim}: we trail everyone (${usScore}) — fix before amplifying anything else.` });
      if (Number(weakest.score) <= 2) out.push({ type: "rival-weakness", text: `${r.claim}: ${weakest.name} is weak (${weakest.score})${weakest.evidence ? ` — evidence: ${weakest.evidence}` : " — UNVERIFIED: get evidence before acting"}.` });
    });
    return out;
  }, [rows]);
  return (
    <div data-testid="tool-M08">
      <p className="text-sm text-zinc-600 mb-5">Score each buying criterion for you and two rivals (1–5). A claim without evidence is marked unverified — that is your research list.</p>
      <div className="space-y-5">
        {rows.map((r, i) => (
          <div key={i} className="border border-zinc-200 p-4" data-testid={`m08-row-${i}`}>
            <input value={r.claim} onChange={setRow(i, "claim")} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black pb-1" data-testid={`m08-claim-${i}`} placeholder="Buying criterion, in the customer's words" />
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4 items-end">
              <Field label="Us (1–5)" value={r.us} onChange={(v) => setRows((s) => s.map((x, xi) => (xi === i ? { ...x, us: v } : x)))} testid={`m08-us-${i}`} />
              {r.rivals.map((rv, ri) => (
                <div key={ri} className="col-span-1 grid grid-cols-2 gap-2">
                  <Field label={`${rv.name} (1–5)`} value={rv.score} onChange={(v) => setRival(i, ri, "score")({ target: { value: v } })} testid={`m08-r${ri}-${i}`} />
                  <label className="block">
                    <span className="text-xs text-zinc-500">Evidence?</span>
                    <input value={rv.evidence} onChange={setRival(i, ri, "evidence")} placeholder="source, date" className="mt-1 w-full border border-zinc-200 px-2 py-1.5 text-xs outline-none focus:border-black" data-testid={`m08-ev-${ri}-${i}`} />
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setRows((s) => [...s, { claim: "", us: 3, rivals: [{ name: "Rival A", score: 3, evidence: "" }, { name: "Rival B", score: 3, evidence: "" }] }])} className="text-sm font-medium underline underline-offset-4" data-testid="m08-add">+ Add criterion</button>
      </div>
      <ToolSection title="Strategic gap map" testid="m08-gaps">
        <div className="space-y-2">
          {gaps.length === 0 && <p className="text-sm text-zinc-500">Score at least one criterion to see gaps.</p>}
          {gaps.map((g, i) => (
            <div key={i} className={`border-l-2 pl-4 py-1.5 text-sm leading-relaxed ${g.type === "our-gap" ? "border-red-600 text-red-900" : g.type === "rival-weakness" ? "border-emerald-600 text-emerald-900" : "border-zinc-400 text-zinc-700"}`} data-testid={`m08-gap-${i}`}>
              <span className="text-xs font-medium uppercase tracking-wide mr-2">{g.type === "our-gap" ? "Fix first" : g.type === "rival-weakness" ? "Attackable" : "Amplify"}</span>{g.text}
            </div>
          ))}
        </div>
      </ToolSection>
      <Note tone="warn">Never build a campaign on an unverified rival weakness. The map separates what you know from what you hope — act on the first, research the second.</Note>
    </div>
  );
}
